import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeftIcon, MapPinIcon, ShieldCheckIcon, FileTextIcon, HistoryIcon, CreditCardIcon, GavelIcon, BuildingIcon, AlertCircleIcon, CheckCircleIcon, GlobeIcon, MapIcon, DownloadIcon, PrinterIcon, XIcon, Loader2Icon, CheckIcon, GripHorizontalIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { samplePlots } from '../data/sampleData';
export function LandRecordDetailsPage({
  plotId,
  navigateTo
}: {
  plotId: string | null;
  navigateTo: (page: string) => void;
}) {
  const {
    language,
    toggleLanguage,
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  // CLO Modal State
  const [cloStep, setCloStep] = useState(0); // 0: closed, 1: summary, 2: payment, 2.5: otp, 3: success, 4: certificate
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedCloId, setGeneratedCloId] = useState('');
  // Modal Drag & Resize State
  const [modalPos, setModalPos] = useState({
    x: 0,
    y: 0
  });
  const [modalSize, setModalSize] = useState({
    width: 1024,
    height: 800
  }); // Default max-w-5xl is roughly 1024px
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragStart = useRef({
    x: 0,
    y: 0,
    modalX: 0,
    modalY: 0,
    width: 0,
    height: 0
  });
  const plot = samplePlots.find(p => p.id === plotId) || samplePlots[0];
  const tabs = [{
    id: 'overview',
    label: t('tabOverview') || 'Overview',
    icon: FileTextIcon
  }, {
    id: 'mutation',
    label: t('tabMutation') || 'Mutation Info',
    icon: FileTextIcon
  }, {
    id: 'tax',
    label: t('tabTax') || 'LD TAX Info',
    icon: CreditCardIcon
  }, {
    id: 'parcelMap',
    label: t('tabParcelMap') || 'Parcel Map',
    icon: MapIcon
  }, {
    id: 'transferHistory',
    label: t('tabTransferHistory') || 'Transfer History',
    icon: HistoryIcon
  }, {
    id: 'disputes',
    label: t('tabDisputes') || 'Case Info',
    icon: GavelIcon
  }, {
    id: 'mortgages',
    label: t('tabMortgages') || 'Mortgages Info',
    icon: BuildingIcon
  }];
  const handleApplyCLO = () => {
    setCloStep(1);
    // Reset modal size/pos when opening
    setModalPos({
      x: 0,
      y: 0
    });
    setModalSize({
      width: Math.min(window.innerWidth * 0.9, 1024),
      height: Math.min(window.innerHeight * 0.9, 800)
    });
  };
  const handleProcessPayment = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setGeneratedCloId(`CLO-${plot.district.substring(0, 3).toUpperCase()}-2026-NEW-${Math.floor(100000 + Math.random() * 900000)}`);
      setCloStep(3);
    }, 2000);
  };
  const closeCLOModal = () => {
    setCloStep(0);
    setIsProcessing(false);
  };
  // Drag and Resize Handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setModalPos({
          x: dragStart.current.modalX + dx,
          y: dragStart.current.modalY + dy
        });
      } else if (isResizing.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setModalSize({
          width: Math.max(400, dragStart.current.width + dx),
          height: Math.max(300, dragStart.current.height + dy)
        });
      }
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      isResizing.current = false;
      document.body.style.userSelect = ''; // Restore text selection
    };
    if (cloStep > 0) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [cloStep]);
  const onHeaderMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      modalX: modalPos.x,
      modalY: modalPos.y,
      width: modalSize.width,
      height: modalSize.height
    };
    document.body.style.userSelect = 'none'; // Prevent text selection while dragging
  };
  const onResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    isResizing.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      modalX: modalPos.x,
      modalY: modalPos.y,
      width: modalSize.width,
      height: modalSize.height
    };
    document.body.style.userSelect = 'none';
  };
  // OTP State
  const [otp, setOtp] = useState(['5', '8', '3', '1']);
  const otpRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };
  return <div className="min-h-screen bg-[#F8FAFB] flex flex-col font-sans" data-id="element-1134">
      {/* Top Government Header Bar */}
      <div className="bg-[#006A4E] text-white py-2 px-4 md:px-8 flex justify-between items-center border-b-4 border-[#DC2626]" data-id="element-1135">
        <div className="flex items-center space-x-3" data-id="element-1136">
          <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Bangladesh Government Logo" className="w-10 h-10 rounded-full object-contain bg-white border-2 border-[#C9A84C]" data-id="element-1137" />
          <div className="flex flex-col" data-id="element-1138">
            <span className="text-sm md:text-base font-bold leading-tight" data-id="element-1139">
              {t('govName')}
            </span>
            <span className="text-xs md:text-sm text-green-100 leading-tight" data-id="element-1140">
              {t('ministryName')}
            </span>
          </div>
        </div>
        <button onClick={toggleLanguage} className="flex items-center space-x-1 text-sm font-medium hover:text-green-200 transition-colors" data-id="element-1141">
          <GlobeIcon className="w-4 h-4" data-id="element-1142" />
          <span data-id="element-1143">{language === 'bn' ? 'English' : 'বাংলা'}</span>
        </button>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-8 py-8 space-y-6" data-id="element-1144">
        <button onClick={() => navigateTo('mouzaMap')} className="flex items-center text-sm font-medium text-gray-500 hover:text-[#006A4E] transition-colors" data-id="element-1145">
          <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1146" />{' '}
          {language === 'bn' ? 'মৌজা মানচিত্রে ফিরে যান' : 'Back to Mouza Map'}
        </button>

        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative overflow-hidden" data-id="element-1147">
          {plot.dispute && <div className="absolute top-0 left-0 w-full h-1 bg-red-500" data-id="element-1148"></div>}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-1149">
            <div className="flex items-center space-x-4" data-id="element-1150">
              <div className="w-16 h-16 bg-[#006A4E] rounded-xl flex items-center justify-center shadow-inner flex-shrink-0" data-id="element-1151">
                <MapPinIcon className="w-8 h-8 text-white" data-id="element-1152" />
              </div>
              <div data-id="element-1153">
                <div className="flex items-center space-x-2 mb-1" data-id="element-1154">
                  <h1 className="text-2xl font-bold text-gray-900" data-id="element-1155">
                    {language === 'bn' ? plot.nameBn : plot.name}
                  </h1>
                  {plot.dispute && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200" data-id="element-1156">
                      <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-1157" />
                      Disputed
                    </span>}
                </div>
                <p className="text-sm text-gray-500 flex items-center" data-id="element-1158">
                  <span className="font-medium text-gray-700 mr-2" data-id="element-1159">
                    ID: {plot.id}
                  </span>
                  <span className="mx-2 text-gray-300" data-id="element-1160">|</span>
                  {language === 'bn' ? plot.districtBn : plot.district},{' '}
                  {language === 'bn' ? plot.upazilaBn : plot.upazila}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto" data-id="element-1161">
              {plot.cloId && <div className="inline-flex items-center px-4 py-2.5 rounded-lg bg-green-50 text-green-800 text-sm font-bold border border-green-200 w-full sm:w-auto justify-center" data-id="element-1162">
                  <ShieldCheckIcon className="w-4 h-4 mr-2" data-id="element-1163" />
                  {plot.cloId}
                </div>}
              <button onClick={handleApplyCLO} className="inline-flex items-center px-4 py-2.5 rounded-lg bg-[#006A4E] text-white text-sm font-bold hover:bg-[#004D3A] transition-colors shadow-sm w-full sm:w-auto justify-center" data-id="element-1164">
                <ShieldCheckIcon className="w-4 h-4 mr-2" data-id="element-1165" />
                {t('applyCLO') || 'Apply for CLO Certificate'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col md:flex-row min-h-[500px]" data-id="element-1166">
          {/* Vertical Tabs (Desktop) / Horizontal Scroll (Mobile) */}
          <div className="w-full md:w-64 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200 flex-shrink-0" data-id="element-1167">
            <div className="flex md:flex-col overflow-x-auto md:overflow-visible hide-scrollbar p-2 md:p-4 gap-1" data-id="element-1168">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-[#006A4E] text-white shadow-md' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`} data-id="element-1169">
                    <Icon className={`w-5 h-5 ${isActive ? 'text-green-100' : 'text-gray-400'}`} data-id="element-1170" />
                    <span data-id="element-1171">{tab.label}</span>
                  </button>;
            })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-1172">
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in duration-300" data-id="element-1173">
                <div className="space-y-8" data-id="element-1174">
                  <div data-id="element-1175">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-1176">
                      <MapPinIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1177" />
                      {t('locationInfo')}
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-4" data-id="element-1178">
                      <div className="grid grid-cols-2 gap-4" data-id="element-1179">
                        <div data-id="element-1180">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1181">
                            {t('district')}
                          </p>
                          <p className="font-semibold text-gray-900" data-id="element-1182">
                            {language === 'bn' ? plot.districtBn : plot.district}
                          </p>
                        </div>
                        <div data-id="element-1183">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1184">
                            {t('upazila')}
                          </p>
                          <p className="font-semibold text-gray-900" data-id="element-1185">
                            {language === 'bn' ? plot.upazilaBn : plot.upazila}
                          </p>
                        </div>
                        <div className="col-span-2" data-id="element-1186">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1187">
                            {t('mouza')}
                          </p>
                          <p className="font-semibold text-gray-900" data-id="element-1188">
                            {language === 'bn' ? plot.mouzaBn : plot.mouza}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-1189">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-1190">
                      <FileTextIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1191" />
                      {t('khatianDetails') || 'Khatian Details'}
                    </h3>
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1192">
                      <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100" data-id="element-1193">
                        <p className="text-xs text-blue-600 uppercase tracking-wider mb-1" data-id="element-1194">
                          {t('khatian')}
                        </p>
                        <p className="text-xl font-bold text-blue-900" data-id="element-1195">
                          {plot.khatian}
                        </p>
                      </div>
                      <div className="bg-purple-50/50 rounded-xl p-4 border border-purple-100" data-id="element-1196">
                        <p className="text-xs text-purple-600 uppercase tracking-wider mb-1" data-id="element-1197">
                          {t('dag')}
                        </p>
                        <p className="text-xl font-bold text-purple-900" data-id="element-1198">
                          {plot.dag}
                        </p>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100" data-id="element-1199">
                      <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-1200">
                        <span className="text-gray-500 text-sm" data-id="element-1201">
                          {t('surveyType') || 'Survey Type'}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-1202">
                          {language === 'bn' ? (plot as any).surveyNameBn || 'বিআরএস' : (plot as any).surveyName || 'BRS'}
                        </span>
                      </div>
                      <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-1203">
                        <span className="text-gray-500 text-sm" data-id="element-1204">
                          {t('ownerName') || 'Owner Name'}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-1205">
                          {language === 'bn' ? (plot as any).ownerNameBn || plot.nameBn : (plot as any).ownerName || plot.name}
                        </span>
                      </div>
                      <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-1206">
                        <span className="text-gray-500 text-sm" data-id="element-1207">
                          {t('fatherName') || 'Father Name'}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-1208">
                          {language === 'bn' ? (plot as any).fatherNameBn || 'অজ্ঞাত' : (plot as any).fatherName || 'Unknown'}
                        </span>
                      </div>
                      <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-1209">
                        <span className="text-gray-500 text-sm" data-id="element-1210">
                          {t('address') || 'Address'}
                        </span>
                        <span className="font-semibold text-gray-900 text-right" data-id="element-1211">
                          {language === 'bn' ? (plot as any).addressBn || `${plot.mouzaBn}, ${plot.upazilaBn}` : (plot as any).address || `${plot.mouza}, ${plot.upazila}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-8" data-id="element-1212">
                  <div data-id="element-1213">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-1214">
                      <ShieldCheckIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1215" />
                      Property Status
                    </h3>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100" data-id="element-1216">
                      <div className="p-4 flex justify-between items-center" data-id="element-1217">
                        <span className="text-gray-600 font-medium" data-id="element-1218">
                          {t('landType')}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold capitalize" data-id="element-1219">
                          {t(plot.type as any)}
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center" data-id="element-1220">
                        <span className="text-gray-600 font-medium" data-id="element-1221">
                          {t('area')}
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-1222">
                          {plot.area} Decimals
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center" data-id="element-1223">
                        <span className="text-gray-600 font-medium" data-id="element-1224">
                          {t('ownershipType')}
                        </span>
                        <span className="font-semibold text-gray-900 capitalize" data-id="element-1225">
                          {t(plot.ownership as any)}
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center" data-id="element-1226">
                        <span className="text-gray-600 font-medium" data-id="element-1227">
                          {t('mutationStatus')}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center ${plot.mutationStatus === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-1228">
                          {plot.mutationStatus === 'completed' && <CheckCircleIcon className="w-3.5 h-3.5 mr-1" data-id="element-1229" />}
                          {t(plot.mutationStatus as any)}
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center" data-id="element-1230">
                        <span className="text-gray-600 font-medium" data-id="element-1231">
                          {t('taxStatus')}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${plot.taxStatus === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1232">
                          {t(plot.taxStatus as any)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

            {/* MUTATION INFO TAB - CARD LAYOUT */}
            {activeTab === 'mutation' && <div className="animate-in fade-in duration-300" data-id="element-1233">
                <div className="flex justify-between items-center mb-6" data-id="element-1234">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1235">
                    {t('tabMutation') || 'Mutation Info'}
                  </h3>
                </div>

                {plot.transferHistory && plot.transferHistory.length > 0 ? <div className="space-y-6" data-id="element-1236">
                    {plot.transferHistory.map((record: any, idx: number) => <div key={record.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1237">
                        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center" data-id="element-1238">
                          <span className="font-bold text-gray-700 flex items-center" data-id="element-1239">
                            <FileTextIcon className="w-4 h-4 mr-2 text-[#006A4E]" data-id="element-1240" />
                            Record #{idx + 1}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1241">
                            {language === 'bn' ? record.typeBn : record.type}
                          </span>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1242">
                          <div data-id="element-1243">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1244">
                              {t('transferDate')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1245">
                              {record.date}
                            </p>
                          </div>
                          <div data-id="element-1246">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1247">
                              {t('transferFrom')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1248">
                              {record.from}
                            </p>
                          </div>
                          <div data-id="element-1249">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1250">
                              {t('transferTo')}
                            </p>
                            <p className="font-medium text-[#006A4E]" data-id="element-1251">
                              {record.to}
                            </p>
                          </div>
                          <div data-id="element-1252">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1253">
                              {t('transferType')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1254">
                              {language === 'bn' ? record.typeBn : record.type}
                            </p>
                          </div>
                          <div data-id="element-1255">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1256">
                              {t('mutCaseNo') || 'Case No.'}
                            </p>
                            <p className="font-mono font-medium text-gray-900" data-id="element-1257">
                              {record.caseNo || record.dolilNo || record.docRef}
                            </p>
                          </div>
                          <div data-id="element-1258">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1259">
                              {t('mutCaseDate') || 'Case Date'}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1260">
                              {record.caseDate || record.dolilDate || record.date}
                            </p>
                          </div>
                          <div data-id="element-1261">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1262">
                              {t('approvalDate') || 'Approval Date'}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1263">
                              {record.approvalDate || '-'}
                            </p>
                          </div>
                          <div data-id="element-1264">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1265">
                              {t('dcrNo') || 'DCR No.'}
                            </p>
                            <p className="font-mono font-medium text-gray-900" data-id="element-1266">
                              {record.dcrNo || '-'}
                            </p>
                          </div>
                          <div data-id="element-1267">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1268">
                              {t('dcrDate') || 'DCR Date'}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1269">
                              {record.dcrDate || '-'}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1270">
                    <FileTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1271" />
                    <p className="text-gray-500 font-medium" data-id="element-1272">
                      {t('noRecordFound')}
                    </p>
                  </div>}
              </div>}

            {/* LD TAX INFO TAB */}
            {activeTab === 'tax' && <div className="animate-in fade-in duration-300" data-id="element-1273">
                <div className="flex justify-between items-center mb-6" data-id="element-1274">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1275">
                    {t('ldTaxHistory') || 'LD TAX Info'}
                  </h3>
                </div>

                {plot.taxHistory && plot.taxHistory.length > 0 ? <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1276">
                    <div className="overflow-x-auto" data-id="element-1277">
                      <table className="w-full text-left text-sm" data-id="element-1278">
                        <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200" data-id="element-1279">
                          <tr data-id="element-1280">
                            <th className="px-6 py-4" data-id="element-1281">{t('taxYear')}</th>
                            <th className="px-6 py-4" data-id="element-1282">{t('amount')}</th>
                            <th className="px-6 py-4" data-id="element-1283">{t('status')}</th>
                            <th className="px-6 py-4" data-id="element-1284">
                              {t('dhakhilaNo') || 'Dhakhila No.'}
                            </th>
                            <th className="px-6 py-4" data-id="element-1285">
                              {t('dhakhilaDate') || 'Dhakhila Date'}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100" data-id="element-1286">
                          {plot.taxHistory.map((record: any) => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-1287">
                              <td className="px-6 py-4 font-medium text-gray-900" data-id="element-1288">
                                {record.year}
                              </td>
                              <td className="px-6 py-4 font-bold text-gray-700" data-id="element-1289">
                                {record.amount}
                              </td>
                              <td className="px-6 py-4" data-id="element-1290">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${record.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1291">
                                  {t(record.status as any)}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-gray-500 font-mono text-xs" data-id="element-1292">
                                {record.dhakhilaNo || record.receiptNo}
                              </td>
                              <td className="px-6 py-4 text-gray-500" data-id="element-1293">
                                {record.dhakhilaDate || record.date}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1294">
                    <CreditCardIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1295" />
                    <p className="text-gray-500 font-medium" data-id="element-1296">
                      {t('noRecordFound')}
                    </p>
                  </div>}
              </div>}

            {/* PARCEL MAP TAB */}
            {activeTab === 'parcelMap' && <div className="animate-in fade-in duration-300" data-id="element-1297">
                <div className="flex justify-between items-center mb-6" data-id="element-1298">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1299">
                    {t('parcelMap') || 'Parcel Map'}
                  </h3>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-1300">
                  <div className="border border-gray-200 rounded-lg overflow-hidden mb-6 bg-white flex justify-center" data-id="element-1301">
                    <img src="https://cdn.magicpatterns.com/uploads/ny2k3HFhyHRGZ8XpcGKfEL/Parcel-map-data.jpg" alt="Parcel Map" className="max-w-full h-auto object-contain max-h-[600px]" data-id="element-1302" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-1303">
                    <div data-id="element-1304">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1305">
                        {t('khatian')}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1306">
                        {plot.khatian}
                      </p>
                    </div>
                    <div data-id="element-1307">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1308">
                        {t('dag')}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1309">
                        {plot.dag}
                      </p>
                    </div>
                    <div data-id="element-1310">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1311">
                        {t('mouza')}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1312">
                        {language === 'bn' ? plot.mouzaBn : plot.mouza}
                      </p>
                    </div>
                    <div data-id="element-1313">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1314">
                        {t('area')}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1315">
                        {plot.area} Decimals
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {/* TRANSFER HISTORY TAB */}
            {activeTab === 'transferHistory' && <div className="animate-in fade-in duration-300" data-id="element-1316">
                <div className="flex justify-between items-center mb-6" data-id="element-1317">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1318">
                    {t('tabTransferHistory') || 'Transfer History'}
                  </h3>
                </div>

                {plot.transferHistory && plot.transferHistory.length > 0 ? <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1319">
                    <div className="bg-[#4CAF50] text-white text-center py-3 font-bold text-lg" data-id="element-1320">
                      {t('transferStatement') || 'হস্তান্তর বৃত্তান্ত'}
                    </div>
                    <div className="overflow-x-auto" data-id="element-1321">
                      <table className="w-full text-center text-sm border-collapse" data-id="element-1322">
                        <thead className="bg-gray-50 text-gray-800 font-medium" data-id="element-1323">
                          <tr data-id="element-1324">
                            <th className="px-4 py-3 border border-gray-200" data-id="element-1325">
                              {t('prevKhatianNo') || 'আগত খতিয়ান নং'}
                            </th>
                            <th className="px-4 py-3 border border-gray-200" data-id="element-1326">
                              {t('dag') || 'দাগ নং'}
                            </th>
                            <th className="px-4 py-3 border border-gray-200" data-id="element-1327">
                              {t('dagLandArea') || 'দাগে জমির পরিমান (একর)'}
                            </th>
                            <th className="px-4 py-3 border border-gray-200" data-id="element-1328">
                              {t('khatianLandArea') || 'খতিয়ানে জমির পরিমাণ (একর)'}
                            </th>
                            <th className="px-4 py-3 border border-gray-200" data-id="element-1329">
                              {t('transferredLandArea') || 'হস্তান্তরিত জমির পরিমাণ (একর)'}
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-1330">
                          {plot.transferHistory.map((record: any, idx: number) => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-1331">
                                <td className="px-4 py-3 border border-gray-200 font-medium text-gray-900" data-id="element-1332">
                                  {record.prevKhatianNo || '-'}
                                </td>
                                <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-1333">
                                  {record.dagNo || plot.dag}
                                </td>
                                <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-1334">
                                  {record.dagLandArea || '-'}
                                </td>
                                <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-1335">
                                  {record.khatianLandArea || '-'}
                                </td>
                                <td className="px-4 py-3 border border-gray-200 font-bold text-gray-900" data-id="element-1336">
                                  {record.transferredLandArea || '-'}
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <div className="border-t border-gray-200" data-id="element-1337">
                      <div className="flex border-b border-gray-200" data-id="element-1338">
                        <div className="w-1/3 md:w-1/4 p-4 border-r border-gray-200 font-bold text-gray-800 flex items-center justify-center text-center" data-id="element-1339">
                          {t('ownershipSource') || 'মালিকানা সূত্র'}
                        </div>
                        <div className="w-2/3 md:w-3/4 p-4 text-gray-900 font-medium flex items-center" data-id="element-1340">
                          {language === 'bn' ? (plot.transferHistory[0] as any).ownershipSourceBn || `${plot.transferHistory[0].typeBn} সূত্রেঃ` : (plot.transferHistory[0] as any).ownershipSource || `By ${plot.transferHistory[0].type}:`}
                        </div>
                      </div>
                      <div className="flex" data-id="element-1341">
                        <div className="w-1/3 md:w-1/4 p-4 border-r border-gray-200 font-bold text-gray-800 flex items-center justify-center text-center" data-id="element-1342">
                          {t('descriptionLabel') || 'বিবরণ'}
                        </div>
                        <div className="w-2/3 md:w-3/4 p-4 text-gray-500 tracking-widest flex items-center" data-id="element-1343">
                          ------------------------
                        </div>
                      </div>
                    </div>
                  </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1344">
                    <HistoryIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1345" />
                    <p className="text-gray-500 font-medium" data-id="element-1346">
                      {t('noRecordFound')}
                    </p>
                  </div>}
              </div>}

            {/* CASE INFO TAB */}
            {activeTab === 'disputes' && <div className="animate-in fade-in duration-300" data-id="element-1347">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1348">
                  {t('caseInfo') || 'Case Info'}
                </h3>

                {plot.litigations && plot.litigations.length > 0 ? <div className="space-y-4" data-id="element-1349">
                    {plot.litigations.map((lit: any) => <div key={lit.id} className="bg-white border border-red-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1350">
                        <div className="bg-red-50 px-6 py-3 border-b border-red-100 flex justify-between items-center" data-id="element-1351">
                          <div className="flex items-center space-x-2" data-id="element-1352">
                            <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-1353" />
                            <span className="font-bold text-red-900" data-id="element-1354">
                              {lit.caseNo}
                            </span>
                          </div>
                          <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold" data-id="element-1355">
                            {language === 'bn' ? lit.statusBn : lit.status}
                          </span>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1356">
                          <div data-id="element-1357">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-1358">
                              {t('court')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1359">
                              {language === 'bn' ? lit.courtBn : lit.court}
                            </p>
                          </div>
                          <div data-id="element-1360">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-1361">
                              {t('dateFiled')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1362">
                              {lit.dateFiled}
                            </p>
                          </div>

                          {lit.nextHearing && lit.nextHearing !== '—' && <div data-id="element-1363">
                              <p className="text-sm text-gray-500 mb-1" data-id="element-1364">
                                {language === 'bn' ? 'পরবর্তী শুনানির তারিখ' : 'Next Hearing'}
                              </p>
                              <p className="font-medium text-amber-600" data-id="element-1365">
                                {lit.nextHearing}
                              </p>
                            </div>}

                          {lit.result && <div className="col-span-1 md:col-span-2" data-id="element-1366">
                              <p className="text-sm text-gray-500 mb-1" data-id="element-1367">
                                {language === 'bn' ? 'ফলাফল' : 'Result'}
                              </p>
                              <p className="font-medium text-green-700" data-id="element-1368">
                                {language === 'bn' && lit.resultBn ? lit.resultBn : lit.result}
                              </p>
                            </div>}

                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-1369">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1370">
                              {t('plaintiff')}
                            </p>
                            <p className="font-bold text-gray-900" data-id="element-1371">
                              {language === 'bn' ? lit.plaintiffBn : lit.plaintiff}
                            </p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-1372">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1373">
                              {t('defendant')}
                            </p>
                            <p className="font-bold text-gray-900" data-id="element-1374">
                              {language === 'bn' ? lit.defendantBn : lit.defendant}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-16 bg-green-50 rounded-xl border border-green-200" data-id="element-1375">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1376">
                      <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-1377" />
                    </div>
                    <h4 className="text-lg font-bold text-green-900 mb-2" data-id="element-1378">
                      Clear Title
                    </h4>
                    <p className="text-green-700" data-id="element-1379">
                      No active litigations or disputes found for this property.
                    </p>
                  </div>}
              </div>}

            {/* MORTGAGES INFO TAB */}
            {activeTab === 'mortgages' && <div className="animate-in fade-in duration-300" data-id="element-1380">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1381">
                  {t('mortgagesInfo') || 'Mortgages Info'}
                </h3>

                {plot.mortgages && plot.mortgages.length > 0 ? <div className="space-y-4" data-id="element-1382">
                    {plot.mortgages.map((mortgage: any) => <div key={mortgage.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1383">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1384">
                          <div className="flex items-center space-x-3" data-id="element-1385">
                            <BuildingIcon className="w-5 h-5 text-gray-500" data-id="element-1386" />
                            <span className="font-bold text-gray-900 text-lg" data-id="element-1387">
                              {language === 'bn' ? mortgage.bankBn : mortgage.bank}
                            </span>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${mortgage.status === 'active' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-1388">
                            {t(mortgage.status as any)}
                          </span>
                        </div>
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-1389">
                          <div data-id="element-1390">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-1391">
                              {t('loanAmount')}
                            </p>
                            <p className="text-xl font-bold text-gray-900" data-id="element-1392">
                              {mortgage.amount}
                            </p>
                          </div>
                          <div data-id="element-1393">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-1394">
                              {t('mortgageDate')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1395">
                              {mortgage.date}
                            </p>
                          </div>
                          <div data-id="element-1396">
                            <p className="text-sm text-gray-500 mb-1" data-id="element-1397">
                              {t('releaseDate')}
                            </p>
                            <p className="font-medium text-gray-900" data-id="element-1398">
                              {mortgage.releaseDate}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1399">
                    <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1400" />
                    <p className="text-gray-500 font-medium" data-id="element-1401">
                      No mortgage records found.
                    </p>
                  </div>}
              </div>}
          </div>
        </div>
      </div>

      {/* CLO APPLICATION MODAL */}
      {cloStep > 0 && <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" data-id="element-1402">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col relative" style={{
        width: `${modalSize.width}px`,
        height: `${modalSize.height}px`,
        transform: `translate(${modalPos.x}px, ${modalPos.y}px)`,
        maxWidth: '100vw',
        maxHeight: '95vh',
        transition: isDragging.current || isResizing.current ? 'none' : 'transform 0.2s ease-out'
      }} data-id="element-1403">
            {/* Modal Header (Draggable) */}
            <div className="bg-[#006A4E] px-6 py-4 flex justify-between items-center text-white flex-shrink-0 cursor-move select-none" onMouseDown={onHeaderMouseDown} data-id="element-1404">
              <h2 className="text-xl font-bold flex items-center" data-id="element-1405">
                <ShieldCheckIcon className="w-6 h-6 mr-2 text-[#C9A84C]" data-id="element-1406" />
                {cloStep === 4 ? t('viewCLOCertificate') || 'CLO Certificate' : t('applyCLO') || 'Apply for CLO Certificate'}
              </h2>
              <button onClick={e => {
            e.stopPropagation();
            closeCLOModal();
          }} className="text-green-100 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10 cursor-pointer" data-id="element-1407">
                <XIcon className="w-6 h-6" data-id="element-1408" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 md:p-8 overflow-y-auto flex-1 relative" data-id="element-1409">
              {/* Step 1: Summary */}
              {cloStep === 1 && <div className="space-y-6 animate-in slide-in-from-right-4 max-w-3xl mx-auto" data-id="element-1410">
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start" data-id="element-1411">
                    <ShieldCheckIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-1412" />
                    <p className="text-sm text-blue-800" data-id="element-1413">
                      {language === 'bn' ? 'আপনি এই প্লটের জন্য একটি ডিজিটাল সার্টিফিকেট অফ ল্যান্ড ওনারশিপ (CLO) তৈরি করতে যাচ্ছেন। এটি একটি আইনগতভাবে বৈধ ডিজিটাল দলিল।' : 'You are about to generate a digital Certificate of Land Ownership (CLO) for this plot. This is a legally valid digital document.'}
                    </p>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-1414">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-bold text-gray-700" data-id="element-1415">
                      {t('khatianDetails') || 'Khatian Details'}
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-y-4 gap-x-6 text-sm" data-id="element-1416">
                      <div data-id="element-1417">
                        <span className="text-gray-500 block mb-1" data-id="element-1418">
                          {t('district')}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-1419">
                          {language === 'bn' ? plot.districtBn : plot.district}
                        </span>
                      </div>
                      <div data-id="element-1420">
                        <span className="text-gray-500 block mb-1" data-id="element-1421">
                          {t('upazila')}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-1422">
                          {language === 'bn' ? plot.upazilaBn : plot.upazila}
                        </span>
                      </div>
                      <div data-id="element-1423">
                        <span className="text-gray-500 block mb-1" data-id="element-1424">
                          {t('mouza')}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-1425">
                          {language === 'bn' ? plot.mouzaBn : plot.mouza}
                        </span>
                      </div>
                      <div data-id="element-1426">
                        <span className="text-gray-500 block mb-1" data-id="element-1427">
                          {t('khatian')}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-1428">
                          {plot.khatian}
                        </span>
                      </div>
                      <div className="col-span-2" data-id="element-1429">
                        <span className="text-gray-500 block mb-1" data-id="element-1430">
                          {t('ownerName') || 'Owner Name'}
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-1431">
                          {language === 'bn' ? (plot as any).ownerNameBn || plot.nameBn : (plot as any).ownerName || plot.name}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl border border-gray-200" data-id="element-1432">
                    <span className="font-bold text-gray-700" data-id="element-1433">
                      {t('cloFee') || 'CLO Generation Fee'}
                    </span>
                    <span className="text-2xl font-bold text-[#006A4E]" data-id="element-1434">
                      ৳ 500
                    </span>
                  </div>

                  <button onClick={() => setCloStep(2)} className="w-full py-3.5 bg-[#006A4E] text-white rounded-xl font-bold text-lg hover:bg-[#004D3A] transition-colors shadow-md" data-id="element-1435">
                    {t('paymentProcessing') || 'Proceed to Payment'}
                  </button>
                </div>}

              {/* Step 2: Payment Gateway */}
              {cloStep === 2 && <div className="space-y-6 animate-in slide-in-from-right-4 max-w-md mx-auto mt-10" data-id="element-1436">
                  <div className="text-center mb-6" data-id="element-1437">
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-1438">
                      Secure Payment Gateway
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-1439">
                      Enter your card details to pay ৳ 500
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-1440">
                    <div data-id="element-1441">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1442">
                        Card Number
                      </label>
                      <div className="relative" data-id="element-1443">
                        <CreditCardIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-1444" />
                        <input type="text" defaultValue="4111 1111 1111 1111" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006A4E] focus:border-[#006A4E] font-mono" data-id="element-1445" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1446">
                      <div data-id="element-1447">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1448">
                          Expiry Date
                        </label>
                        <input type="text" defaultValue="12/28" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006A4E] focus:border-[#006A4E] font-mono" data-id="element-1449" />
                      </div>
                      <div data-id="element-1450">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1451">
                          CVV
                        </label>
                        <input type="password" defaultValue="123" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006A4E] focus:border-[#006A4E] font-mono" data-id="element-1452" />
                      </div>
                    </div>

                    <button onClick={() => setCloStep(2.5)} className="w-full py-3.5 mt-4 bg-[#006A4E] text-white rounded-xl font-bold text-lg hover:bg-[#004D3A] transition-colors shadow-md flex justify-center items-center" data-id="element-1453">
                      {`${t('payNow') || 'Pay Now'} ৳ 500`}
                    </button>
                    <button onClick={() => setCloStep(1)} className="w-full py-2 text-gray-500 font-medium hover:text-gray-700 text-sm" data-id="element-1454">
                      Cancel and go back
                    </button>
                  </div>
                </div>}

              {/* Step 2.5: OTP Verification */}
              {cloStep === 2.5 && <div className="space-y-6 animate-in slide-in-from-right-4 max-w-md mx-auto mt-10" data-id="element-1455">
                  <div className="text-center mb-6" data-id="element-1456">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1457">
                      OTP Verification
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-1458">
                      An OTP has been sent to your registered mobile number{' '}
                      <br data-id="element-1459" />
                      <span className="font-bold text-gray-700" data-id="element-1460">
                        +880171****678
                      </span>
                    </p>
                  </div>

                  <div className="space-y-6" data-id="element-1461">
                    <div className="flex justify-center space-x-4" data-id="element-1462">
                      {otp.map((digit, index) => <input key={index} ref={otpRefs[index]} type="text" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className="w-14 h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-[#006A4E] focus:ring-2 focus:ring-[#006A4E]/20 transition-colors" data-id="element-1463" />)}
                    </div>

                    <button onClick={handleProcessPayment} disabled={isProcessing || otp.some(d => !d)} className="w-full py-3.5 bg-[#006A4E] text-white rounded-xl font-bold text-lg hover:bg-[#004D3A] transition-colors shadow-md flex justify-center items-center disabled:opacity-70" data-id="element-1464">
                      {isProcessing ? <>
                          <Loader2Icon className="w-5 h-5 mr-2 animate-spin" data-id="element-1465" />
                          Processing...
                        </> : 'Verify OTP'}
                    </button>

                    <div className="text-center space-y-3" data-id="element-1466">
                      <button className="text-[#006A4E] font-medium text-sm hover:underline" data-id="element-1467">
                        Resend OTP
                      </button>
                      <div className="block" data-id="element-1468">
                        <button onClick={() => setCloStep(2)} disabled={isProcessing} className="text-gray-500 font-medium hover:text-gray-700 text-sm" data-id="element-1469">
                          Go Back
                        </button>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* Step 3: Success */}
              {cloStep === 3 && <div className="text-center space-y-6 py-16 animate-in zoom-in-95" data-id="element-1470">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1471">
                    <CheckIcon className="w-12 h-12 text-green-600" data-id="element-1472" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900" data-id="element-1473">
                    {t('paymentSuccess') || 'Payment Successful!'}
                  </h3>
                  <p className="text-gray-500 max-w-sm mx-auto text-lg" data-id="element-1474">
                    Your payment of ৳ 500 has been processed successfully. Your
                    CLO certificate is now ready.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg inline-block text-sm text-gray-600 mb-4" data-id="element-1475">
                    Transaction ID:{' '}
                    <span className="font-mono font-bold text-gray-900" data-id="element-1476">
                      TXN-{Math.floor(Math.random() * 100000000)}
                    </span>
                  </div>
                  <div data-id="element-1477">
                    <button onClick={() => setCloStep(4)} className="px-8 py-3.5 bg-[#006A4E] text-white rounded-xl font-bold text-lg hover:bg-[#004D3A] transition-colors shadow-md inline-flex items-center" data-id="element-1478">
                      <FileTextIcon className="w-5 h-5 mr-2" data-id="element-1479" />
                      {t('viewCLOCertificate') || 'View CLO Certificate'}
                    </button>
                  </div>
                </div>}

              {/* Step 4: Certificate View */}
              {cloStep === 4 && <div className="animate-in fade-in h-full flex flex-col" data-id="element-1480">
                  <div className="flex justify-end mb-4 space-x-3 flex-shrink-0" data-id="element-1481">
                    <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm transition-colors" data-id="element-1482">
                      <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1483" />
                      Download PDF
                    </button>
                    <button className="flex items-center px-4 py-2 bg-[#006A4E] text-white rounded-lg hover:bg-[#004D3A] font-medium text-sm transition-colors shadow-sm" data-id="element-1484">
                      <PrinterIcon className="w-4 h-4 mr-2" data-id="element-1485" />
                      Print
                    </button>
                  </div>

                  {/* The Certificate */}
                  <div className="border-[12px] border-[#006A4E] p-1 bg-white relative mx-auto w-full max-w-4xl shadow-xl" data-id="element-1486">
                    <div className="border-[4px] border-[#C9A84C] p-8 md:p-12 relative overflow-hidden" data-id="element-1487">
                      {/* Watermark */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none" data-id="element-1488">
                        <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Watermark" className="w-96 h-96 object-contain" data-id="element-1489" />
                      </div>

                      <div className="relative z-10" data-id="element-1490">
                        <div className="flex justify-between items-start mb-8" data-id="element-1491">
                          <img src="https://cdn.magicpatterns.com/uploads/iWSbHRFjNdqxbSvrrHtxib/bdlogo.png" alt="Gov Logo" className="w-20 h-20 object-contain" data-id="element-1492" />
                          <div className="text-right" data-id="element-1493">
                            <p className="text-sm font-bold text-gray-600 uppercase tracking-widest mb-1" data-id="element-1494">
                              Government of Bangladesh
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-1495">
                              Ministry of Land
                            </p>
                          </div>
                        </div>

                        <div className="text-center mb-10" data-id="element-1496">
                          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#006A4E] mb-2" data-id="element-1497">
                            Certificate of Land Ownership
                          </h2>
                          <p className="text-gray-500 tracking-widest uppercase text-sm font-semibold" data-id="element-1498">
                            Digital Land Record System
                          </p>
                        </div>

                        <div className="flex justify-between items-end border-b-2 border-gray-200 pb-4 mb-8" data-id="element-1499">
                          <div data-id="element-1500">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1501">
                              Certificate No.
                            </p>
                            <p className="font-mono font-bold text-lg text-gray-900" data-id="element-1502">
                              {generatedCloId}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-1503">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1504">
                              Date of Issue
                            </p>
                            <p className="font-bold text-gray-900" data-id="element-1505">
                              {new Date().toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-8 mb-12" data-id="element-1506">
                          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1507">
                            This is to certify that{' '}
                            <span className="font-bold text-gray-900 border-b border-gray-400 px-2" data-id="element-1508">
                              {language === 'bn' ? (plot as any).ownerNameBn || plot.nameBn : (plot as any).ownerName || plot.name}
                            </span>{' '}
                            is the registered owner of the land parcel described
                            below, as per the digital land records of the
                            Government of Bangladesh.
                          </p>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-gray-50 p-6 rounded-lg border border-gray-200" data-id="element-1509">
                            <div data-id="element-1510">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1511">
                                District
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-1512">
                                {language === 'bn' ? plot.districtBn : plot.district}
                              </p>
                            </div>
                            <div data-id="element-1513">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1514">
                                Upazila
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-1515">
                                {language === 'bn' ? plot.upazilaBn : plot.upazila}
                              </p>
                            </div>
                            <div data-id="element-1516">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1517">
                                Mouza
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-1518">
                                {language === 'bn' ? plot.mouzaBn : plot.mouza}
                              </p>
                            </div>
                            <div data-id="element-1519">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1520">
                                Land Type
                              </p>
                              <p className="font-bold text-gray-900 capitalize" data-id="element-1521">
                                {t(plot.type as any)}
                              </p>
                            </div>
                            <div data-id="element-1522">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1523">
                                Khatian No.
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-1524">
                                {plot.khatian}
                              </p>
                            </div>
                            <div data-id="element-1525">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1526">
                                Dag No.
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-1527">
                                {plot.dag}
                              </p>
                            </div>
                            <div className="col-span-2" data-id="element-1528">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1529">
                                Total Area
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-1530">
                                {plot.area} Decimals
                              </p>
                            </div>
                          </div>

                          {/* New Sections: Tax, Case, Mortgage */}
                          <div className="space-y-6" data-id="element-1531">
                            {/* LD TAX Clearance */}
                            <div data-id="element-1532">
                              <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-200 pb-1" data-id="element-1533">
                                LD TAX Payment Status
                              </h4>
                              {plot.taxHistory && plot.taxHistory.length > 0 ? <div className={`p-4 rounded-lg border ${plot.taxHistory[0].status === 'paid' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-id="element-1534">
                                  {plot.taxHistory[0].status === 'paid' ? <div className="flex items-center text-green-800 font-medium mb-2" data-id="element-1535">
                                      <CheckCircleIcon className="w-5 h-5 mr-2" data-id="element-1536" />
                                      All land development taxes are cleared
                                    </div> : <div className="flex items-center text-red-800 font-medium mb-2" data-id="element-1537">
                                      <AlertCircleIcon className="w-5 h-5 mr-2" data-id="element-1538" />
                                      Tax payment pending for{' '}
                                      {plot.taxHistory[0].year}
                                    </div>}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3" data-id="element-1539">
                                    <div data-id="element-1540">
                                      <span className="text-gray-500 block" data-id="element-1541">
                                        Latest Year
                                      </span>
                                      <span className="font-semibold text-gray-900" data-id="element-1542">
                                        {plot.taxHistory[0].year}
                                      </span>
                                    </div>
                                    <div data-id="element-1543">
                                      <span className="text-gray-500 block" data-id="element-1544">
                                        Amount
                                      </span>
                                      <span className="font-semibold text-gray-900" data-id="element-1545">
                                        {plot.taxHistory[0].amount}
                                      </span>
                                    </div>
                                    <div data-id="element-1546">
                                      <span className="text-gray-500 block" data-id="element-1547">
                                        Status
                                      </span>
                                      <span className={`font-semibold ${plot.taxHistory[0].status === 'paid' ? 'text-green-700' : 'text-red-700'}`} data-id="element-1548">
                                        {plot.taxHistory[0].status.toUpperCase()}
                                      </span>
                                    </div>
                                    <div data-id="element-1549">
                                      <span className="text-gray-500 block" data-id="element-1550">
                                        Dhakhila No.
                                      </span>
                                      <span className="font-mono font-semibold text-gray-900" data-id="element-1551">
                                        {plot.taxHistory[0].dhakhilaNo || '-'}
                                      </span>
                                    </div>
                                  </div>
                                </div> : <p className="text-sm text-gray-500 italic" data-id="element-1552">
                                  No tax records available.
                                </p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1553">
                              {/* Litigation Status */}
                              <div data-id="element-1554">
                                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-200 pb-1" data-id="element-1555">
                                  Litigation Status
                                </h4>
                                {plot.litigations && plot.litigations.length > 0 ? <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg" data-id="element-1556">
                                    <div className="flex items-start text-amber-800" data-id="element-1557">
                                      <AlertCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-1558" />
                                      <div data-id="element-1559">
                                        <p className="font-bold mb-1" data-id="element-1560">
                                          Active Litigation Found
                                        </p>
                                        <p className="text-sm" data-id="element-1561">
                                          Case No:{' '}
                                          <span className="font-mono" data-id="element-1562">
                                            {plot.litigations[0].caseNo}
                                          </span>
                                        </p>
                                        <p className="text-sm" data-id="element-1563">
                                          Court:{' '}
                                          {language === 'bn' ? plot.litigations[0].courtBn : plot.litigations[0].court}
                                        </p>
                                      </div>
                                    </div>
                                  </div> : <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center text-gray-700" data-id="element-1564">
                                    <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" data-id="element-1565" />
                                    <span className="font-medium text-sm" data-id="element-1566">
                                      No active litigations or disputes.
                                    </span>
                                  </div>}
                              </div>

                              {/* Mortgage Status */}
                              <div data-id="element-1567">
                                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3 border-b border-gray-200 pb-1" data-id="element-1568">
                                  Mortgage Information
                                </h4>
                                {plot.mortgages && plot.mortgages.some((m: any) => m.status === 'active') ? <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg" data-id="element-1569">
                                    <div className="flex items-start text-blue-800" data-id="element-1570">
                                      <BuildingIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-1571" />
                                      <div data-id="element-1572">
                                        <p className="font-bold mb-1" data-id="element-1573">
                                          Active Mortgage Found
                                        </p>
                                        <p className="text-sm" data-id="element-1574">
                                          Bank:{' '}
                                          {language === 'bn' ? plot.mortgages.find((m: any) => m.status === 'active').bankBn : plot.mortgages.find((m: any) => m.status === 'active').bank}
                                        </p>
                                        <p className="text-sm" data-id="element-1575">
                                          Amount:{' '}
                                          {plot.mortgages.find((m: any) => m.status === 'active').amount}
                                        </p>
                                      </div>
                                    </div>
                                  </div> : <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center text-gray-700" data-id="element-1576">
                                    <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" data-id="element-1577" />
                                    <span className="font-medium text-sm" data-id="element-1578">
                                      No active mortgages.
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-between items-end mt-16" data-id="element-1579">
                          <div className="w-28 h-28 bg-white border-2 border-gray-200 p-1 flex items-center justify-center" data-id="element-1580">
                            {/* Real Sample QR Code */}
                            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://land.gov.bd/verify/CLO-2026-99812" alt="QR Code" className="w-full h-full object-contain opacity-90" data-id="element-1581" />
                          </div>
                          <div className="text-center" data-id="element-1582">
                            <div className="w-48 border-b border-gray-400 mb-2" data-id="element-1583"></div>
                            <p className="text-xs font-bold text-gray-600 uppercase tracking-widest" data-id="element-1584">
                              Digital Signature
                            </p>
                            <p className="text-xs text-gray-500 mt-1" data-id="element-1585">
                              Ministry of Land
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Resize Handle */}
            <div className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize flex items-end justify-end p-1 text-gray-400 hover:text-gray-600 z-50" onMouseDown={onResizeMouseDown} data-id="element-1586">
              <GripHorizontalIcon className="w-4 h-4 transform -rotate-45" data-id="element-1587" />
            </div>
          </div>
        </div>}
    </div>;
}